import { TRANSACTION_ACTION_TYPES } from './types';
import transactionService from '../services/transactionService';

/**
 * Get user transactions with pagination action (without dispatch)
 * @param {Object} params - Query parameters object
 * @param {number} params.limit - Number of transactions to fetch (default: 5)
 * @param {number} params.skip - Number of transactions to skip (default: 0)
 * @returns {Promise<Object>} Transactions result with data and pagination info
 */
export const getTransactions = async (params = {}) => {
    try {
        const response = await transactionService.getTransactions(params);
        
        if (response.success && response.data) {
            return {
                success: true,
                data: response.data
            };
        } else {
            return { success: false, error: response.message || 'Failed to fetch transactions' };
        }
    } catch (error) {
        return { success: false, error: error.message || 'An unexpected error occurred' };
    }
};

/**
 * Get balance summary action (without dispatch)
 * @returns {Promise<Object>} Balance summary result with data
 */
export const getBalanceSummary = async () => {
    try {
        const response = await transactionService.getBalanceSummary();
        
        if (response.success && response.data) {
            return {
                success: true,
                data: response?.data?.data
            };
        } else {
            return { success: false, error: response.message || 'Failed to fetch balance summary' };
        }
    } catch (error) {
        return { success: false, error: error.message || 'An unexpected error occurred' };
    }
};

/**
 * Get today earnings summary action (without dispatch)
 * @param {string} startDate - Start date
 * @param {string} endDate - End date
 * @returns {Promise<Object>} Today earnings summary result with data
 */
export const getTodayEarningsSummary = async (startDate, endDate) => {
    try {
        const response = await transactionService.getTodayEarningsSummary(startDate, endDate);
        
        if (response.success && response.data) {
            return {
                success: true,
                data: response?.data?.data || response?.data
            };
        } else {
            return { success: false, error: response.message || 'Failed to fetch today earnings summary' };
        }
    } catch (error) {
        return { success: false, error: error.message || 'An unexpected error occurred' };
    }
};

/**
 * Get daily earnings breakdown action (without dispatch)
 * @returns {Promise<Object>} Daily earnings breakdown result with data
 */
export const getDailyEarnings = async () => {
    try {
        const response = await transactionService.getDailyEarnings();
        
        if (response.success && response.data) {
            return {
                success: true,
                data: response?.data?.data || response?.data
            };
        } else {
            return { success: false, error: response.message || 'Failed to fetch daily earnings' };
        }
    } catch (error) {
        return { success: false, error: error.message || 'An unexpected error occurred' };
    }
};

/**
 * Get monthly earnings breakdown action (without dispatch)
 * @param {Object} params - Query parameters object
 * @param {number} params.limit - Number of months to fetch (default: 10)
 * @param {number} params.skip - Number of months to skip (default: 0)
 * @param {number} params.year - Year to fetch earnings for
 * @param {number} params.month - Month to fetch earnings for (1-12)
 * @returns {Promise<Object>} Monthly earnings breakdown result with data
 */
export const getMonthlyEarnings = async (params = {}) => {
    try {
        const response = await transactionService.getMonthlyEarnings(params);
        
        if (response.success && response.data) {
            return {
                success: true,
                data: response?.data?.data || response?.data
            };
        } else {
            return { success: false, error: response.message || 'Failed to fetch monthly earnings' };
        }
    } catch (error) {
        return { success: false, error: error.message || 'An unexpected error occurred' };
    }
};

/**
 * Get total earnings breakdown action (without dispatch)
 * @returns {Promise<Object>} Total earnings breakdown result with data
 */
export const getTotalEarningsBreakdown = async () => {
    try {
        const response = await transactionService.getTotalEarningsBreakdown();
        
        if (response.success && response.data) {
            return {
                success: true,
                data: response?.data?.data || response?.data
            };
        } else {
            return { success: false, error: response.message || 'Failed to fetch total earnings breakdown' };
        }
    } catch (error) {
        return { success: false, error: error.message || 'An unexpected error occurred' };
    }
};

/**
 * Get total earnings yearly action (without dispatch)
 * @param {number|string} year - Year to fetch earnings for
 * @returns {Promise<Object>} Total earnings yearly result with data
 */
export const getTotalEarningsYearly = async (year) => {
    try {
        const response = await transactionService.getTotalEarningsYearly(year);
        
        if (response.success && response.data) {
            return {
                success: true,
                data: response?.data?.data || response?.data
            };
        } else {
            return { success: false, error: response.message || 'Failed to fetch total earnings yearly' };
        }
    } catch (error) {
        return { success: false, error: error.message || 'An unexpected error occurred' };
    }
};

/**
 * Request payout action (without dispatch)
 * @returns {Promise<Object>} Payout request result with status and message
 */
export const requestPayout = async () => {
    try {
        const response = await transactionService.requestPayout();
        
        if (response.success) {
            return {
                success: true,
                status: response.status,
                message: response.message,
                data: response.data
            };
        } else {
            return { 
                success: false, 
                error: response.message || 'Failed to request payout',
                status: response.status
            };
        }
    } catch (error) {
        // Handle error response with requirements
        if (error.requirements) {
            return {
                success: false,
                error: error.message || 'Failed to request payout',
                requirements: error.requirements,
                status: error.status
            };
        }
        return { 
            success: false, 
            error: error.message || 'An unexpected error occurred',
            status: error.status
        };
    }
};

/**
 * Submit W-9 tax form information (without dispatch)
 * @param {Object} formData - W-9 form payload
 * @returns {Promise<Object>} W-9 form submission result with status and message
 */
export const createW9Form = async (formData) => {
    try {
        const response = await transactionService.createW9Form(formData);

        if (response.success) {
            return {
                success: true,
                status: response.status,
                message: response.message,
                data: response.data?.data || response?.data
            };
        } else {
            return {
                success: false,
                error: response.message || 'Failed to submit W-9 form',
                status: response.status
            };
        }
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || error.message || 'An unexpected error occurred',
            errors: error?.error?.details || null,
            status: error.response?.status || error.status,
            data: error.response?.data || null
        };
    }
};

/**
 * Submit W-8 tax form information (without dispatch)
 * @param {Object} formData - W-8 form payload
 * @returns {Promise<Object>} W-8 form submission result with status and message
 */
export const createW8Form = async (formData) => {
    try {
        const response = await transactionService.createW8Form(formData);

        if (response.success) {
            return {
                success: true,
                status: response.status,
                message: response.message,
                data: response.data?.data || response?.data
            };
        } else {
            return {
                success: false,
                error: response.message || 'Failed to submit W-8 form',
                status: response.status
            };
        }
    } catch (error) {
        return {
            success: false,
            message: error.response?.data?.message || error.message || 'An unexpected error occurred',
            errors: error?.error?.details || null,
            status: error.response?.status || error.status,
            data: error.response?.data || null
        };
    }
};

/**
 * Get W-9 tax form information (without dispatch)
 * @returns {Promise<Object>} W-9 form result with data
 */
export const getW9Form = async () => {
    try {
        const response = await transactionService.getW9Form();
        
        if (response.success && response.data) {
            return {
                success: true,
                data: response.data
            };
        } else {
            return { success: false, error: response.message || 'Failed to fetch W-9 form' };
        }
    } catch (error) {
        return { success: false, error: error.message || 'An unexpected error occurred' };
    }
};

/**
    * Get W-8 tax form information (without dispatch)
 * @returns {Promise<Object>} W-8 form result with data
 */
export const getW8Form = async () => {
    try {
        const response = await transactionService.getW8Form();
        
        if (response.success && response.data) {
            return {
                success: true,
                data: response.data
            };
        } else {
            return { success: false, error: response.message || 'Failed to fetch W-8 form' };
        }
    } catch (error) {
        return { success: false, error: error.message || 'An unexpected error occurred' };
    }
};

/**
 * Lookup user information for masspay action (Redux thunk)
 * @returns {Function} Redux thunk function
 */
export const lookupUser = () => async (dispatch) => {
    dispatch({ type: TRANSACTION_ACTION_TYPES.LOOKUP_USER_REQUEST });
    
    try {
        const response = await transactionService.lookupUser();
        
        if (response.success && response.data) {
            dispatch({
                type: TRANSACTION_ACTION_TYPES.LOOKUP_USER_SUCCESS,
                payload: {
                    data: response.data,
                }
            });

            return {
                success: true,
                data: response.data,
                status: response.status
            };
        } else {
            dispatch({
                type: TRANSACTION_ACTION_TYPES.LOOKUP_USER_FAILURE,
                payload: {
                    error: response.message || 'Failed to lookup user',
                    status: response.status
                }
            });

            return { 
                success: false, 
                error: response.message || 'Failed to lookup user',
                status: response.status
            };
        }
    } catch (error) {
        dispatch({
            type: TRANSACTION_ACTION_TYPES.LOOKUP_USER_FAILURE,
            payload: {
                error: error.response?.data?.message || error.message || 'An unexpected error occurred',
                status: error.response?.status || error.status
            }
        });

        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'An unexpected error occurred',
            status: error.response?.status || error.status,
            data: error.response?.data || null
        };
    }
};

/**
 * List services for masspay action (Redux thunk)
 * @returns {Function} Redux thunk function
 */
export const listServices = () => async (dispatch) => {
    dispatch({ type: TRANSACTION_ACTION_TYPES.LIST_SERVICES_REQUEST });
    
    try {
        const response = await transactionService.listServices();
        
        if (response.success && response.data) {
            dispatch({
                type: TRANSACTION_ACTION_TYPES.LIST_SERVICES_SUCCESS,
                payload: {
                    data: response.data,
                }
            });

            return {
                success: true,
                data: response.data,
                status: response.status
            };
        } else {
            dispatch({
                type: TRANSACTION_ACTION_TYPES.LIST_SERVICES_FAILURE,
                payload: {
                    error: response.message || 'Failed to fetch services list',
                    status: response.status
                }
            });

            return { 
                success: false, 
                error: response.message || 'Failed to fetch services list',
                status: response.status
            };
        }
    } catch (error) {
        dispatch({
            type: TRANSACTION_ACTION_TYPES.LIST_SERVICES_FAILURE,
            payload: {
                error: error.response?.data?.message || error.message || 'An unexpected error occurred',
                status: error.response?.status || error.status
            }
        });

        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'An unexpected error occurred',
            status: error.response?.status || error.status,
            data: error.response?.data || null
        };
    }
};

/**
 * Get masspay attributes action (without dispatch)
 * @param {Object} params - Query parameters object
 * @param {string} params.currency - Currency code (e.g., 'USD')
 * @param {string} params.destination_token - Destination token
 * @param {string} params.user_token - User token
 * @returns {Promise<Object>} Masspay attributes result with data
 */
export const getMasspayAttributes = async (params = {}) => {
    try {
        const response = await transactionService.getMasspayAttributes(params);
        
        if (response.success && response.data) {
            return {
                success: true,
                data: response.data
            };
        } else {
            return { success: false, error: response.message || 'Failed to fetch masspay attributes' };
        }
    } catch (error) {
        return { success: false, error: error.message || 'An unexpected error occurred' };
    }
};

/**
 * Get payment method information action (without dispatch)
 * @returns {Promise<Object>} Payment method result with data
 */
export const getPaymentMethod = async () => {
    try {
        const response = await transactionService.getPaymentMethod();
        
        if (response.success && response.data) {
            return {
                success: true,
                data: response.data?.data
            };
        } else {
            return { success: false, error: response.message || 'Failed to fetch payment method' };
        }
    } catch (error) {
        return { success: false, error: error.message || 'An unexpected error occurred' };
    }
};

/**
 * Send change payout method confirmation link (without dispatch)
 * @returns {Promise<Object>} Send link result with status/message
 */
export const sendPaymentMethodChangeLink = async () => {
    try {
        const response = await transactionService.sendPaymentMethodChangeLink();

        if (response.success) {
            return {
                success: true,
                status: response.status,
                message: response.message,
                data: response.data
            };
        }

        return {
            success: false,
            error: response.message || 'Failed to send confirmation email',
            status: response.status
        };
    } catch (error) {
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'An unexpected error occurred',
            status: error.response?.status || error.status,
            data: error.response?.data || null
        };
    }
};

/**
 * Validate payout method change code (without dispatch)
 * @param {string} code - Change code from URL query param
 * @returns {Promise<Object>} Validation result with status/message
 */
export const validatePaymentMethodChangeCode = async (code) => {
    try {
        if (!code) {
            return { success: false, error: 'Missing change code' };
        }

        const response = await transactionService.validatePaymentMethodChangeCode(code);

        if (response.success) {
            return {
                success: true,
                status: response.status,
                message: response.message,
                data: response.data
            };
        }

        return {
            success: false,
            error: response.message || 'Failed to validate change code',
            status: response.status
        };
    } catch (error) {
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'An unexpected error occurred',
            status: error.response?.status || error.status,
            data: error.response?.data || null
        };
    }
};

/**
 * Set masspay attributes action (without dispatch)
 * @param {Object} attributesData - Attributes data object
 * @param {string} attributesData.user_token - User token
 * @param {string} attributesData.destination_token - Destination token
 * @param {string} attributesData.currency - Currency code (e.g., 'USD')
 * @param {string} attributesData.company - Company name
 * @param {string} attributesData.payer - Payer name
 * @param {Array} attributesData.attributes - Array of attribute objects with token and value
 * @returns {Promise<Object>} Set attributes result with status and message
 */
export const createMasspayAccount = async (attributesData) => {
    try {
        const response = await transactionService.createMasspayAccount(attributesData);
        
        if (response.success) {
            return {
                success: true,
                status: response.status,
                message: response.message,
                data: response.data
            };
        } else {
            return { 
                success: false, 
                error: response.message || 'Failed to set masspay attributes',
                status: response.status
            };
        }
    } catch (error) {
        return { 
            success: false, 
            error: error.response?.data?.message || error.message || 'An unexpected error occurred',
            status: error.response?.status || error.status,
            data: error.response?.data || null
        };
    }
};

/**
 * Get transaction history action (without dispatch)
 * @param {Object} params - Query parameters object
 * @param {number} params.number_of_records - Number of records to fetch
 * @param {string} params.start_date - Start date (YYYY-MM-DD format)
 * @param {string} params.end_date - End date (YYYY-MM-DD format)
 * @param {number} params.page - Page number
 * @returns {Promise<Object>} Transaction history result with data
 */
export const getTransactionHistory = async (params = {}) => {
    try {
        const response = await transactionService.getTransactionHistory(params);
        
        if (response.success && response.data) {
            return {
                success: true,
                data: response.data?.data
            };
        } else {
            return { success: false, error: response.message || 'Failed to fetch transaction history' };
        }
    } catch (error) {
        return { success: false, error: error.message || 'An unexpected error occurred' };
    }
};

/**
 * Export earnings report as PDF action (without dispatch)
 * @param {string} html - HTML content to convert to PDF
 * @returns {Promise<Object>} PDF export result with blob data
 */
export const exportEarningsPdf = async (html) => {
    try {
        const response = await transactionService.exportEarningsPdf(html);
        
        if (response.success && response.data) {
            // Create download link and trigger download
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `earnings-report-${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
            
            return {
                success: true,
                message: 'PDF downloaded successfully'
            };
        } else {
            return { success: false, error: response.message || 'Failed to export PDF' };
        }
    } catch (error) {
        return { success: false, error: error.message || 'An unexpected error occurred' };
    }
};

